/**
 * Simplified UI Mode Toggle
 * GarageBand-inspired toggle to hide advanced controls for beginners
 */

import { motion } from 'framer-motion';
import { Eye, EyeOff, Sparkles } from 'lucide-react';
import { Button } from '../../ui/button';

export const SimplifiedModeToggle = ({ 
  isSimplified, 
  onToggle,
  className = '' 
}) => {
  return (
    <Button
      variant={isSimplified ? 'default' : 'ghost'}
      size="sm"
      onClick={onToggle}
      className={`relative p-2 ${
        isSimplified 
          ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/50' 
          : 'text-zinc-400 hover:text-white'
      } ${className}`}
      data-testid="simplified-mode-toggle"
      title={isSimplified ? 'Switch to Advanced Mode' : 'Switch to Simple Mode'}
    >
      {isSimplified ? (
        <Eye className="w-5 h-5" />
      ) : (
        <EyeOff className="w-5 h-5" />
      )}
      {isSimplified && (
        <motion.span
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          className="absolute -top-1 -right-1 w-3 h-3 bg-emerald-500 rounded-full flex items-center justify-center"
        >
          <Sparkles className="w-2 h-2 text-white" />
        </motion.span>
      )}
    </Button>
  );
};

// Features hidden in simplified mode
export const SIMPLIFIED_HIDDEN_FEATURES = [
  'automation',      // Automation lanes
  'midiDevices',     // External MIDI device panel
  'clipEditMode',    // Split/Join tools
  'snapToggle',      // Snap to grid toggle
  'exportFormat',    // Export format selection
  'countIn',         // Count-in settings
  'timeSignature',   // Time signature selection
  'metronomeVolume', // Metronome volume slider
  'trackEffects',    // Per-track effects (EQ, reverb, delay)
  'mixView',         // Mix view tab
  'chordTrack',      // Chord track
  'smartTempo',      // Smart tempo detection
];

// Features always visible (even in simplified mode)
export const ALWAYS_VISIBLE_FEATURES = [
  'transport',       // Play/Stop/Record
  'tracks',          // Track list
  'volume',          // Master & track volume
  'mute',            // Mute button
  'solo',            // Solo button
  'metronome',       // Metronome toggle
  'bpm',             // BPM display/control
  'save',            // Save project
  'export',          // Export (simplified)
  'instruments',     // Virtual instruments
  'undo',            // Undo/Redo
];

export default SimplifiedModeToggle;
